import React, { useState, useRef, useEffect, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import useSWRImmutable from "swr/immutable";
import axios from "axios";
import { FaFilter } from "react-icons/fa";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";

const PATH = process.env.REACT_APP_ROOF_PATH_TO_BACKEND;
const fetcher = async (url) => {
  try {
    const res = await axios.get(url, { timeout: 10000 }); // Таймаут 10 секунд
    console.log("Ответ от", url, res.data);
    return res.data;
  } catch (error) {
    console.error("Ошибка fetcher:", {
      message: error.message,
      code: error.code,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error; // Пробрасываем ошибку для useSWR
  }
};

const synonyms = {
  классик: "classic",
  кредо: "kredo",
  пкфтв: "grand",
  дшту: "line",
  кк: "rr",
  кфд: "ral",
  лкувщ: "kredo",
  сдфыышс: "clasic",
  лфьуф: "kamea",
  лмштеф: "kvinta",
  ч: "x",
  здгы: "plus",
  ghjayfcnbk: "профнастил",
  профлист: "профнастил"
};



function SearchableDropdown({ onSelect }) {
  const [inputValue, setInputValue] = useState("");
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [filters, setFilters] = useState({
    collection: "",
    thickness: "",
    coating: "",
    color: "",
  });
  const [isEditing, setIsEditing] = useState(false); // Новый флаг для отслеживания редактирования

  const dropdownRef = useRef(null);
  const textareaRef = useRef(null);

  const { data, error, isLoading } = useSWRImmutable(`${PATH}/api/nomenclature`, fetcher, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    dedupingInterval: 60000, // Кэшировать на 1 минуту
    errorRetryCount: 2,
    timeout: 15000, // Таймаут 15 секунд
  });

  const replaceSynonyms = (input) => {
    const words = input.toLowerCase().split(" ");
    return words.map((word) => synonyms[word] || word).join(" ");
  };

  useEffect(() => {
    console.log(data)
  }, [data])

  const options = useMemo(() => {
    return (
      data?.values?.map(
        ({
          value,
          collection,
          thickness,
          coating,
          color,
          id_bitrix,
          id_thickness,
          id_coating,
          id_color,
          id_collection,
          price,
          recommended_price,
        }) => ({
          value,
          label: value,
          collection,
          thickness,
          coating,
          color,
          id_bitrix,
          id_thickness,
          id_coating,
          id_color,
          id_collection,
          price,
          recommended_price,
        })
      ) || []
    );
  }, [data]);

  const filteredOptions = useMemo(() => {
    let filtered = options;

    if (inputValue.trim()) {
      const normalizedInput = replaceSynonyms(inputValue);
      const searchWords = normalizedInput
        .toLowerCase()
        .split(" ")
        .filter(Boolean);

      filtered = filtered.filter((option) => {
        const labelWords = option.label.toLowerCase().split(" ");
        return searchWords.every((searchWord) =>
          labelWords.some((labelWord) => labelWord.includes(searchWord))
        );
      });
    }

    filtered = filtered.filter((option) => {
      return (
        (!filters.collection || option.collection === filters.collection) &&
        (!filters.thickness || option.thickness === filters.thickness) &&
        (!filters.coating || option.coating === filters.coating) &&
        (!filters.color || option.color === filters.color)
      );
    });

    return filtered;
  }, [inputValue, options, filters]);

  const handleSelect = (option) => {
    setInputValue(option.label);
    setSelectedOption(option);
    setIsOptionsOpen(false);
    setIsEditing(false); // Сбрасываем флаг редактирования
    if (onSelect) onSelect(option);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterKey]: value,
    }));
    setSelectedOption(null);
    setInputValue("");
    setIsEditing(false); // Сбрасываем флаг редактирования при изменении фильтров
  };

  const handleResetFilters = () => {
    setFilters({
      collection: "",
      thickness: "",
      coating: "",
      color: "",
    });
    setSelectedOption(null);
    setInputValue("");
    setIsEditing(false); // Сбрасываем флаг редактирования
  };

  const getFilterOptions = (filterKey) => {
    const uniqueValues = [
      ...new Set(options.map((option) => option[filterKey])),
    ];
    return uniqueValues.filter((value) => {
      const tempFilters = { ...filters, [filterKey]: value };
      const tempFilteredOptions = options.filter((option) => {
        return (
          (!tempFilters.collection ||
            option.collection === tempFilters.collection) &&
          (!tempFilters.thickness ||
            option.thickness === tempFilters.thickness) &&
          (!tempFilters.coating || option.coating === tempFilters.coating) &&
          (!tempFilters.color || option.color === tempFilters.color)
        );
      });
      return tempFilteredOptions.length > 0;
    });
  };

  // Автоматический выбор единственного варианта только если не редактируется
  useEffect(() => {
    if (filteredOptions.length === 1 && !isEditing) {
      handleSelect(filteredOptions[0]);
    }
  }, [filteredOptions, isEditing]);

  // Закрытие выпадающих списков при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOptionsOpen(false);
        setIsFiltersOpen(false);
        // Сбрасываем только если пользователь не редактирует и значение не соответствует опциям
        if (!isEditing) {
          const foundOption = options.find(
            (option) => option.label === inputValue
          );
          if (!foundOption) {
            setInputValue("");
            setSelectedOption(null);
            if (onSelect) onSelect(null);
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [inputValue, options, onSelect, isEditing]);

  useEffect(() => {
    console.log("Данные от /api/nomenclature:", { data, error });
    if (data) {
      console.log("Значения data.values:", data?.values);
      console.log("Полученные опции:", options);
    }
    if (error) {
      console.error("Ошибка при запросе:", error);
    }
  }, [data, error, options]);

  // Автоматическое изменение высоты textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [inputValue]);

  return (
    <div className="dropdown w-100 position-relative" ref={dropdownRef}>
      <div className="d-flex align-items-center mb-3">
        <Dropdown
          show={isFiltersOpen}
          onToggle={(isOpen) => {
            setIsFiltersOpen(isOpen);
            if (isOpen) setIsOptionsOpen(false);
          }}
        >
          <Dropdown.Toggle
            variant="outline-secondary"
            id="dropdown-filter"
            style={{
              padding: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaFilter />
          </Dropdown.Toggle>

          <Dropdown.Menu style={{ padding: "10px", minWidth: "250px" }}>
            <Form>
            <Form.Group className="mb-3">
                <Form.Label>Коллекция</Form.Label>
                <Form.Select
                  value={filters.collection}
                  onChange={(e) =>
                    handleFilterChange("collection", e.target.value)
                  }
                >
                  <option value="">Выберите коллекцию</option>
                  {getFilterOptions("collection").map((collection) => (
                    <option key={collection} value={collection}>
                      {collection}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Толщина</Form.Label>
                <Form.Select
                  value={filters.thickness}
                  onChange={(e) =>
                    handleFilterChange("thickness", e.target.value)
                  }
                >
                  <option value="">Выберите толщину</option>
                  {getFilterOptions("thickness").map((thickness) => (
                    <option key={thickness} value={thickness}>
                      {thickness}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Покрытие</Form.Label>
                <Form.Select
                  value={filters.coating}
                  onChange={(e) =>
                    handleFilterChange("coating", e.target.value)
                  }
                >
                  <option value="">Выберите покрытие</option>
                  {getFilterOptions("coating").map((coating) => (
                    <option key={coating} value={coating}>
                      {coating}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Цвет</Form.Label>
                <Form.Select
                  value={filters.color}
                  onChange={(e) => handleFilterChange("color", e.target.value)}
                >
                  <option value="">Выберите цвет</option>
                  {getFilterOptions("color").map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Button
                variant="outline-danger"
                onClick={handleResetFilters}
                className="w-100"
              >
                Сбросить
              </Button>
            </Form>
          </Dropdown.Menu>
        </Dropdown>

        <textarea
          ref={textareaRef}
          className="form-control flex-grow-1"
          placeholder="Введите название..."
          value={inputValue}
          onChange={(e) => {
            const newValue = e.target.value;
            setInputValue(newValue);
            setIsEditing(true);
            if (!newValue.trim()) {
              handleResetFilters();
            }
          }}
          onFocus={() => {
            setIsOptionsOpen(true);
            setIsFiltersOpen(false);
          }}
          rows={1}
          style={{
            resize: "none",
            overflow: "hidden",
            whiteSpace: "normal",
            wordWrap: "break-word",
            marginRight: "10px",
          }}
        />
      </div>

      {isOptionsOpen && (
  <ul
    className="dropdown-menu show"
    style={{
      width: "100%",
      maxHeight: "200px",
      overflowY: "auto",
      position: "absolute",
      zIndex: 1050,
      whiteSpace: "normal",
      wordWrap: "break-word",
      left: "0",
      right: "0",
      minWidth: "100%",
    }}
  >
    {isLoading ? (
      <li>
        <span className="dropdown-item text-muted">Загрузка...</span>
      </li>
    ) : error ? (
      <li>
        <span className="dropdown-item text-danger">
          ❌ Ошибка загрузки данных
        </span>
      </li>
    ) : filteredOptions.length > 0 ? (
      filteredOptions.map((option) => (
        <li key={option.value}>
          <button
            className="dropdown-item"
            onClick={() => handleSelect(option)}
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
            }}
          >
            {option.label}
          </button>
        </li>
      ))
    ) : (
      <li>
        <span className="dropdown-item text-muted">
          Ничего не найдено
        </span>
      </li>
    )}
  </ul>
)}
    </div>
  );
}

export default SearchableDropdown;